import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionCard } from "@/components/shared/PageChrome";
import { useAuth } from "@/contexts/AuthContext";

const DECISIONS = [["REVIEWED_NO_OBJECTION", "Reviewed – no objection (not adoption)"], ["NEEDS_INFORMATION", "Needs information"], ["NOT_SUITABLE", "Not suitable for adoption"]];

export function LegacyAdoptionReviewPanel({ bookingId }) {
  const { memberships } = useAuth();
  const isAdmin = (memberships || []).some((m) => m.org_type === "INTERNAL" && m.role === "JIGSAW_ADMIN");
  const [view, setView] = useState(null);
  const [decision, setDecision] = useState("NEEDS_INFORMATION");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/governance/legacy-adoption/${bookingId}/review`).then((r) => setView(r.data)).catch(() => setView(null));
  useEffect(() => { if (isAdmin) load(); }, [bookingId, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isAdmin || !view) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/governance/legacy-adoption/${bookingId}/review-decisions`, { decision, reason });
      toast.success("Review decision recorded (adoption remains blocked)");
      setReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record decision"); } finally { setBusy(false); }
  };
  return (
    <SectionCard title="Adoption review (GAP-07) — review only" subtitle="Inspect the historical record and record an audited opinion. This never adopts, never waives holds and unlocks nothing." testId="legacy-adoption-review" bodyClassName="px-5 pb-4 space-y-3">
      <div data-testid="adoption-state" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800">
        Adoption state: <strong>{view.adoption_state.state}</strong> — blocking: {view.adoption_state.blocking.join(", ")}
      </div>
      <div className="grid gap-2 text-[12px] sm:grid-cols-2">
        <div><span className="text-jigsaw-muted">Source:</span> {view.source_identity.system} · {view.source_identity.legacy_reference} · {view.source_identity.snapshot}</div>
        <div><span className="text-jigsaw-muted">Held records:</span> {Object.entries(view.held_records).map(([k, v]) => `${k} ${v}`).join(" · ")}</div>
      </div>
      <ul className="divide-y divide-jigsaw-border rounded-md border border-jigsaw-border text-[12px]">
        {view.requirements.map((r) => (
          <li key={r.key} data-testid={`adoption-req-${r.key}`} data-ok={r.ok} className="flex items-start gap-2 px-3 py-1.5">
            <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${r.ok ? "bg-emerald-500" : "bg-red-500"}`} /><span className="font-medium">{r.key}</span><span className="text-jigsaw-muted">{r.detail}</span>
          </li>
        ))}
      </ul>
      <div className="text-[12px]">
        <p className="font-medium text-jigsaw-navy">Stays (planned dates from the legacy source — not actual)</p>
        {view.stays.map((s) => <p key={s.id} data-testid={`adoption-stay-${s.id}`} className="text-jigsaw-muted">{s.check_in_date} → {s.check_out_date} · {s.room_count} room(s) · {s.planned_state}</p>)}
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <Select value={decision} onValueChange={setDecision}><SelectTrigger data-testid="adoption-decision-select"><SelectValue /></SelectTrigger>
          <SelectContent>{DECISIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
        <Textarea data-testid="adoption-reason-input" rows={2} className="sm:col-span-2" placeholder="Reason (required, audited)" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <Button data-testid="adoption-decision-save" size="sm" onClick={submit} disabled={busy || reason.trim().length < 5}>Record review decision</Button>
      {view.reviews.length > 0 && (
        <ul data-testid="adoption-reviews" className="text-[12px] text-jigsaw-muted">
          {view.reviews.map((r) => <li key={r.id}>{new Date(r.at).toLocaleString("en-GB")} · {r.decision} · {r.reason}</li>)}
        </ul>
      )}
    </SectionCard>
  );
}
